// #region Imports
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Theme, useTheme } from "@mui/material";
import Alert, { AlertProps } from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Portal from "@mui/material/Portal";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { enumKeyOf, enumParse } from "_shared/_utilities/enum-utils";
import { MRTNoMemo } from "_shared/_views/tables/table";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import axios from "axios";
import {
  DocumentError,
  getAllDocumentErrors,
  getAreErrorsLoadingDocumentsByIds,
  getDocumentsByIds,
  getLoadingDocumentIds,
} from "documents/_stores/documents-redux-slice";
import Document from "documents/document";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import {
  EntityAgreementError,
  getAllEntityAgreementErrors,
  getAreErrorsLoadingEntityAgreementsByIds,
  getEntityAgreementsByIds,
  getLoadingEntityAgreementIds,
} from "legal-entities/agreements/_stores/entity-agreements-redux-slice";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import {
  getIndividualsByEntity,
  getIndividualsByUserIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByEntityIdQuery, useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  type MRT_Cell as Cell,
  type MRT_ColumnDef as ColumnDef,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_Row as Row,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import {
  ForumError,
  getAllForumErrors,
  getAreErrorsLoadingForumsByIds,
  getForumsByIds,
  getLoadingForumIds,
} from "messaging/forums/_stores/forums-redux-slice";
import Forum from "messaging/forums/forum";
import {
  addPermissionTemplate,
  getErrorLoadingPermissionTemplatesByQuery,
  getIsLoadingPermissionTemplatesByQuery,
  getPermissionTemplatesByQuery,
  populatePermissionTemplatesByQuery,
  removePermissionTemplate,
  updatePermissionTemplate,
} from "permissions/_stores/permission-templates-redux-slice";
import {
  addPermission,
  addPermissions,
  disablePermission,
  enablePermission,
  getErrorLoadingPermissionsByGranteeId,
  getIsLoadingPermissionsByGranteeId,
  getPermissionsByGranteeId,
  getSessionPermissions,
  populatePermissionsByGranteeId,
  populateSessionPermissions,
} from "permissions/_stores/permissions-redux-slice";
import AddPermissionDialog from "permissions/_views/dialogs/add-permission-dialog";
import { FilterOption } from "permissions/_views/hooks/use-autocomplete-filter-props";
import PermissionsFilterDrawer from "permissions/_views/tables/_components/permissions-filter-drawer";
import PermissionsToolbar from "permissions/_views/tables/_components/permissions-toolbar";
import usePermissionsQuerySync from "permissions/_views/tables/_hooks/use-permissions-query-sync";
import usePopulateRelatedEntities from "permissions/_views/tables/_hooks/use-populate-related-entities";
import {
  childResourceConstructors,
  clampByCategory,
  classNameFromResource,
  clearPermissionFilters,
  computeChildVisibility,
  computeTopLevelVisibility,
  getDocumentName,
  getEntityAgreementName,
  getForumName,
  getProposalName,
  getWorkAgreementName,
  isPermissionsRow,
  isTemplateRow,
  isTopLevelParent,
  sumPermissionCounts,
} from "permissions/_views/tables/_utilities/utils";
import ApplyTemplateMenuButton from "permissions/_views/tables/cards/apply-template-menu-button";
import DescriptionCell, { DescriptionCellHandle } from "permissions/_views/tables/cards/description-cell";
import GranteeNameCell from "permissions/_views/tables/cards/grantee-name-cell";
import ManageTemplateRowActions from "permissions/_views/tables/cards/manage-template-row-actions";
import PermissionCard from "permissions/_views/tables/cards/permission-card";
import NameCell, { NameCellHandle } from "permissions/_views/tables/cells/name-cell";
import SingleGranteeResourceCell from "permissions/_views/tables/cells/single-grantee-resource-cell";
import {
  AlertBarContainer,
  AlertBarSelectionContainer,
  MobileButton,
  NoRowsText,
  ToolbarButtons,
  ToolbarButtonsContainer,
} from "permissions/_views/tables/permissions-table-styles";
import {
  AccessTypeEnumByResourceType,
  CardType,
  ControlMeta,
  GroupedCellConfig,
  MetaControlType,
  PermissionProps,
  SortedColumnDef,
  TableRow,
  TopLevelTemplatesIndex,
  UniversalCellConfig,
} from "permissions/_views/types/permission-types";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import Permission from "permissions/permission";
import DocumentSubjectMatterGroupCell from "permissions/resources/documents/_views/document-subject-matter-permission-resource-group-cell";
import {
  DocumentAccess,
  DocumentContentsAccess,
} from "permissions/resources/documents/documents-permissions-resources";
import { DocumentSubjectMatter } from "permissions/resources/documents/types";
import LegalEntityGroupCell from "permissions/resources/legal-entities/_views/legal-entity-permission-resource-group-cell";
import {
  EntityAgreementAccess,
  LegalEntityAccess,
} from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/types";
import { ForumAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import type { Resource } from "permissions/resources/permission-resource";
import { AccessLevel, AnyResourceJSON, ResourceConstructor, Scope } from "permissions/resources/types";
import { resourceFromJSON } from "permissions/resources/utils";
import ProposalGroupCell from "permissions/resources/work/_views/proposal-permission-resource-group-cell";
import WorkAgreementGroupCell from "permissions/resources/work/_views/work-agreement-permission-resource-group-cell";
import { ProposalAccess, WorkAgreementAccess } from "permissions/resources/work/work-permissions-resources";
import PermissionTemplate from "permissions/templates/permission-template";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { getProposalsByIds } from "work/proposals/_store/proposals-redux-slice";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";
import {
  getAllWorkAgreementErrors,
  getAreErrorsLoadingWorkAgreementsByIds,
  getLoadingWorkAgreementIds,
  getWorkAgreementsByIds,
  WorkAgreementError,
} from "work/work-agreements/_store/work-agreement-redux-slice";
// #endregion

const columnHelper = createMRTColumnHelper<TableRow>();

export default function PermissionsTable(props: Readonly<PermissionProps>) {
  // Refs
  const nameCellRef = useRef<NameCellHandle | null>(null);
  const descriptionCellRef = useRef<DescriptionCellHandle | null>(null);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const theme = useTheme();
  const confirm = useConfirmDialog();
  const {
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    activeCategory,
    setActiveCategory,
    permissionsQuery,
    permissionTemplatesQuery,
    clearToMainPermissionsView,
  } = usePermissionsQuerySync();

  // Redux dispatch & dialogs
  const dispatch = useAttorneyHubDispatch();
  const { openDialog } = useDialog();

  // Responsive queries
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // State
  const [creatingRow, setCreatingRow] = useState<boolean>(false);
  const [editingRow, setEditingRow] = useState<Row<TableRow> | null>(null);
  const [templateName, setTemplateName] = useState<string>("");
  const [templateDescription, setTemplateDescription] = useState<string>("");
  const [templateResources, setTemplateResources] = useState<Resource[]>([]);
  const [nameIsValid, setNameIsValid] = useState(false);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [granteeOptions, setGranteeOptions] = useState<FilterOption[]>([]);

  const hasParent = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("hasParent") === "true";
  }, [location.search]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<AlertProps["severity"]>("error");

  const isSingleGranteeView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has("granteeUserId");
  }, [location.search]);

  const isManageTemplatesView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("action") === "manage-templates";
  }, [location.search]);

  const hasAdditionalFilters: boolean =
    new URLSearchParams(location.search).has("categories") ||
    new URLSearchParams(location.search).has("granteeUserIds") ||
    new URLSearchParams(location.search).has("accessType") ||
    new URLSearchParams(location.search).has("documentIds") ||
    new URLSearchParams(location.search).has("proposalIds") ||
    new URLSearchParams(location.search).has("workAgreementIds");

  const topLevelTemplates: TopLevelTemplatesIndex = useMemo(() => {
    const byConstructor = new Map<ResourceConstructor, Resource>();
    const legalByAccessType = new Map<string, Resource>();
    const docContentsBySubject = new Map<string, Resource>();

    for (const resource of Permission.topLevelResources()) {
      const Constructor = resource.constructor as ResourceConstructor;
      if (!byConstructor.has(Constructor)) byConstructor.set(Constructor, resource);

      if (resource instanceof LegalEntityAccess) {
        const key = enumKeyOf(LegalEntityAccessType, resource.accessType);
        if (!legalByAccessType.has(key)) legalByAccessType.set(key, resource);
      }

      if (resource instanceof DocumentContentsAccess) {
        const key = enumKeyOf(DocumentSubjectMatter, resource.subjectMatter);
        if (!docContentsBySubject.has(key)) docContentsBySubject.set(key, resource);
      }
    }

    return {
      byConstructor,
      legalByAccessType,
      docContentsBySubject,
    };
  }, []);

  const topLevelResources = useMemo(
    () =>
      Array.from(topLevelTemplates.byConstructor.keys()).sort((a, b) =>
        classNameFromResource(a).localeCompare(classNameFromResource(b), undefined, { sensitivity: "base" })
      ),
    [topLevelTemplates.byConstructor]
  );

  // Session permissions
  const sessionPermissions = useSelector(getSessionPermissions);
  const isLoadingSessionPermissions = useSelector((state: RootState) => state.permissions.session.loading);
  const wasErrorLoadingSessionPermissions = useSelector((state: RootState) => state.permissions.session.error);


  // Permissions query
  const permissionsResponse = useSelector(getPermissionsByGranteeId(permissionsQuery));
  const isLoadingPermissions = useSelector(getIsLoadingPermissionsByGranteeId(permissionsQuery));
  const permissionsLoadErrors = useSelector(getErrorLoadingPermissionsByGranteeId(permissionsQuery));

  // Permission Templates query
  const permissionTemplatesResponse = useSelector(getPermissionTemplatesByQuery(permissionTemplatesQuery));
  const isLoadingPermissionTemplates = useSelector(getIsLoadingPermissionTemplatesByQuery(permissionTemplatesQuery));
  const permissionTemplatesLoadErrors = useSelector(
    getErrorLoadingPermissionTemplatesByQuery(permissionTemplatesQuery)
  );

  function showToast(message: string, severity: AlertProps["severity"]) {
    setToastMsg(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }

  const entityMembers = useSelector(getIndividualsByEntity(session.user!.legalEntityId));

  useEffect(() => {
    let canceled = false;
    async function fetchGranteeOptions() {
      const options: FilterOption[] = [];
      for (const member of entityMembers ?? []) {
        try {
          options.push({
            value: member.userId.value,
            label: member.fullName,
          });
        } catch (error) {
          console.error(error);
          showToast(`Failed to load grantee option for entity member ${member.userId.value}`, "error");
        }
      }
      if (!canceled) setGranteeOptions(options);
    }
    fetchGranteeOptions();
    return () => {
      canceled = true;
    };
  }, [entityMembers]);

  useEffect(() => {
    if (!sessionPermissions && !isLoadingSessionPermissions && !wasErrorLoadingSessionPermissions) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, isLoadingSessionPermissions, wasErrorLoadingSessionPermissions, dispatch, session])

  useEffect(() => {
    if (permissionsQuery && !isLoadingPermissions && !permissionsLoadErrors && !permissionsResponse) {
      dispatch(
        populatePermissionsByGranteeId({
          session,
          query: permissionsQuery,
        })
      ).unwrap()
      .catch((err) => {
        showToast(err.message ?? "Failed to load permissions", "error");
      });
    }

    if (
      permissionTemplatesQuery &&
      !isLoadingPermissionTemplates &&
      !permissionTemplatesLoadErrors &&
      !permissionTemplatesResponse
    ) {
      dispatch(
        populatePermissionTemplatesByQuery({
          session,
          query: permissionTemplatesQuery,
        })
      ).unwrap()
      .catch((err) => {
        showToast(err.message ?? "Failed to load permission templates", "error");
      });
    }
  }, [
    permissionsResponse,
    permissionsQuery,
    permissionTemplatesQuery,
    dispatch,
    isLoadingPermissions,
    permissionsLoadErrors,
    isLoadingPermissionTemplates,
    permissionTemplatesLoadErrors,
    permissionTemplatesResponse,
    session
  ]);

  const { isRefetching, userIds, documentIds, forumIds, proposalIds, workAgreementIds, entityAgreementIds } =
    usePopulateRelatedEntities(permissionsResponse);

  // Load related entities
  const individuals: Individual[] = useSelector(getIndividualsByUserIds(userIds));
  const { isLoading: isLoadingIndividuals, isError: areErrorsLoadingIndividuals } = useGetIndividualsByUserIdsQuery(
    { userIds: new Set(userIds.map((id) => id.value)) },
    { skip: userIds.length === 0 },
  );
  const loadingIndividualUserIds: Guid[] = useMemo(
    () => (isLoadingIndividuals ? userIds : []),
    [isLoadingIndividuals, userIds]
  );

  const documents: Array<Document | UnavailableDocument> = useSelector(getDocumentsByIds(documentIds));
  const loadingDocumentIds: Guid[] = useSelector(getLoadingDocumentIds);
  const areErrorsLoadingDocuments: boolean = useSelector(getAreErrorsLoadingDocumentsByIds(documentIds));
  const allDocumentErrors: DocumentError[] = useSelector(getAllDocumentErrors);

  const forums: Forum[] = useSelector(getForumsByIds(forumIds));
  const loadingForumIds: Guid[] = useSelector(getLoadingForumIds);
  const areErrorsLoadingForums: boolean = useSelector(getAreErrorsLoadingForumsByIds(forumIds));
  const allForumErrors: ForumError[] = useSelector(getAllForumErrors);

  const barebonesProposals: BarebonesProposalResponse[] = useSelector(getProposalsByIds(proposalIds));
  const proposalQuery = useGetProposalsByIdsQuery(
    { proposalIds: proposalIds.map(id => id.value) },
    { skip: proposalIds.length === 0 }
  );

  const barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[] = useSelector(
    getWorkAgreementsByIds(workAgreementIds)
  );
  const loadingWorkAgreementIds: Guid[] = useSelector(getLoadingWorkAgreementIds);
  const areErrorsLoadingWorkAgreements: boolean = useSelector(getAreErrorsLoadingWorkAgreementsByIds(workAgreementIds));
  const allWorkAgreementErrors: WorkAgreementError[] = useSelector(getAllWorkAgreementErrors);

  const entityAgreements: EntityAgreement[] = useSelector(getEntityAgreementsByIds(entityAgreementIds));
  const loadingEntityAgreementIds = useSelector(getLoadingEntityAgreementIds);
  const areErrorsLoadingEntityAgreements: boolean = useSelector(
    getAreErrorsLoadingEntityAgreementsByIds(entityAgreementIds)
  );
  const allEntityAgreementErrors: EntityAgreementError[] = useSelector(getAllEntityAgreementErrors);

  const individualByUserId = useMemo(() => {
    const map = new Map<string, Individual>();
    for (const i of individuals) map.set(i.userId.value, i);
    return map;
  }, [individuals]);

  const documentById = useMemo(() => {
    const map = new Map<string, Document | UnavailableDocument>();
    for (const d of documents) map.set(d.id.value, d);
    return map;
  }, [documents]);

  const proposalById = useMemo(() => {
    const map = new Map<string, BarebonesProposalResponse>();
    for (const p of barebonesProposals) {
      if (!p.id) continue;
      map.set(p.id, p);
    }
    return map;
  }, [barebonesProposals]);

  const workAgreementById = useMemo(() => {
    const map = new Map<string, BarebonesWorkAgreementInfoAPIResponse>();
    for (const w of barebonesWorkAgreements) {
      if (!w.id) continue;
      map.set(w.id, w);
    }
    return map;
  }, [barebonesWorkAgreements]);

  const loadingUserIdsSet = useMemo(
    () => new Set(loadingIndividualUserIds.map((u) => u.value)),
    [loadingIndividualUserIds]
  );

  const individualErrorByUserId = useMemo(() => {
    const m = new Map<string, string | null>();
    if (areErrorsLoadingIndividuals) for (const uid of userIds) m.set(uid.value, "Failed to load individual");
    return m;
  }, [areErrorsLoadingIndividuals, userIds]);

  // Autocomplete filter options
  useGetIndividualsByEntityIdQuery({ legalEntityId: session.user!.legalEntityId.value });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if ((creatingRow || isManageTemplatesView) && params.has("granteeUserId")) {
      params.delete("granteeUserId");
      navigate({ search: params.toString() });
    }
  }, [creatingRow, isManageTemplatesView, location.search, navigate]);

  const openAddPermissionDialog = useCallback(() => {
    openDialog({
      resetStack: true,
      component: <AddPermissionDialog />,
      title: "Add Permission",
      MuiProps: { maxWidth: "md" },
    });
  }, [openDialog]);

  const handleTogglePermission = useCallback(
    (permission?: Permission) => {
      if (!permission) return console.warn("handleTogglePermission: Permission not found");
      if (permission.isVirtual) {
        dispatch(addPermission({ session, permission }))
          .unwrap()
          .catch((error) => showToast(error, "error"));
        return;
      }

      const isDisabled = Boolean(permission.disabledDate);
      const action = isDisabled
        ? enablePermission({ session, permission })
        : disablePermission({ session, permission });

      dispatch(action)
        .unwrap()
        .catch((error) => showToast(error, "error"));
    },
    [dispatch, session]
  );

  const handleToggleTemplateResource = useCallback((resource?: Resource) => {
    if (!resource) {
      console.warn("handleToggleTemplateResource: Resource not found");
      return;
    }

    if (!creatingRow && (!editingRow || !isTemplateRow(editingRow?.original ?? null))) return;

    const existingResource = templateResources.find((r) => r.isEqualTo(resource));
    if (existingResource) {
      const ResourceClass = existingResource.constructor as ResourceConstructor;
      const updatedResource = new ResourceClass({
        ...existingResource,
        isDisabled: !existingResource.isDisabled,
      });
      setTemplateResources((prev) => prev.map((r) => (r.isEqualTo(existingResource) ? updatedResource : r)));
    } else {
      const ResourceClass = resource.constructor as ResourceConstructor;
      const newResource = new ResourceClass({
        ...resource,
        isDisabled: !resource.isDisabled,
      });
      setTemplateResources((prev) => prev.map((r) => (r.isEqualTo(newResource) ? newResource : r)));
    }
  }, [creatingRow, editingRow, templateResources]);

  const handleChangeScope = useCallback((permission: Permission, newScope: number | undefined) => {
    if (newScope === undefined) return;
    if (!("scope" in permission.resource)) return;

    const ResourceClass = permission.resource.constructor as ResourceConstructor;
    const updatedResource = new ResourceClass({
      ...permission.resource,
      scope: newScope,
    });

    const updatedPermission = new Permission({
      ...permission,
      resource: updatedResource,
    });

    dispatch(addPermission({ session, permission: updatedPermission }))
      .unwrap()
      .catch((error) => showToast(error, "error"));
  }, [dispatch, session]);

  const handleChangeAccessLevel = useCallback((permission: Permission, newAccessLevel: number | undefined) => {
    if (!permission.resource || newAccessLevel === undefined) return;
    if (!("accessLevel" in permission.resource)) return;

    const ResourceClass = permission.resource.constructor as ResourceConstructor;
    const updatedResource = new ResourceClass({
      ...permission.resource,
      accessLevel: newAccessLevel,
    });

    const updatedPermission = new Permission({
      ...permission,
      resource: updatedResource,
    });

    dispatch(addPermission({ session, permission: updatedPermission }))
      .unwrap()
      .catch((error) => showToast(error, "error"));
  }, [dispatch, session]);

  const handleChangeAccessType = useCallback((permission: Permission, newAccessType: number | undefined) => {
    if (!permission.resource || newAccessType === undefined) return;
    if (!("accessType" in permission.resource)) return;

    const ResourceClass = permission.resource.constructor as ResourceConstructor;
    const updatedResource = new ResourceClass({
      ...permission.resource,
      accessType: newAccessType,
    });

    const updatedPermission = new Permission({
      ...permission,
      resource: updatedResource,
    });

    dispatch(addPermission({ session, permission: updatedPermission }))
      .unwrap()
      .catch((error) => showToast(error, "error"));
  }, [dispatch, session]);

  const handleClearFiltersClicked = useCallback(() => {
    navigate({ search: clearPermissionFilters(location.search) });
  }, [location.search, navigate]);

  const applyAccessControlChange = useCallback((effective: Permission, resource: Resource, meta: ControlMeta, newValue: string) => {
    if (meta.type === MetaControlType.Scope && "scope" in resource) {
      const updatedValue = enumParse(Scope, newValue) as number | undefined;
      handleChangeScope(effective, updatedValue);
      return;
    }
    if (meta.type === MetaControlType.AccessLevel && "accessLevel" in resource) {
      const updatedValue = enumParse(AccessLevel, newValue) as number | undefined;
      handleChangeAccessLevel(effective, updatedValue);
    }
    if (meta.type === MetaControlType.AccessType && "accessType" in resource) {
      const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
      const updatedValue = enumParse(en, newValue) as number | undefined;
      handleChangeAccessType(effective, updatedValue);
    }
  }, [handleChangeScope, handleChangeAccessLevel, handleChangeAccessType]);

  const handleTemplateAccessControlChange = useCallback((resource: Resource, meta: ControlMeta, newValue: string) => {
    let updatedResource = resourceFromJSON(resource.toJSON() as AnyResourceJSON);

    if (!updatedResource) {
      console.warn("handleTemplateAccessControlChange: Unable to clone resource");
      return;
    }

    if (meta.type === MetaControlType.Scope && "scope" in resource) {
      const scopeKey = enumParse(Scope, newValue) as number | undefined;
      if (scopeKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse scope");
        return;
      }
      if ("scope" in updatedResource) {
        const ResourceClass = updatedResource.constructor as ResourceConstructor;
        updatedResource = new ResourceClass({
          ...updatedResource,
          scope: scopeKey,
        });
      }
    }

    if (meta.type === MetaControlType.AccessLevel && "accessLevel" in resource) {
      const accessLevelKey = enumParse(AccessLevel, newValue) as number | undefined;
      if (accessLevelKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse access level");
        return;
      }
      if ("accessLevel" in updatedResource) {
        const ResourceClass = updatedResource.constructor as ResourceConstructor;
        updatedResource = new ResourceClass({
          ...updatedResource,
          accessLevel: accessLevelKey,
        });
      }
    }

    if (meta.type === MetaControlType.AccessType && "accessType" in resource) {
      const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
      const accessTypeKey = enumParse(en, newValue) as number | undefined;
      if (accessTypeKey === undefined) {
        console.warn("handleTemplateAccessControlChange: Unable to parse access type");
        return;
      }
      if ("accessType" in updatedResource) {
        const ResourceClass = updatedResource.constructor as ResourceConstructor;
        updatedResource = new ResourceClass({
          ...updatedResource,
          accessType: accessTypeKey,
        });
      }
    }

    setTemplateResources((prev) => prev.map((r) => (r.isEqualTo(resource) ? updatedResource : r)));
  }, []);

  const resetState = useCallback((table: TableInstance<TableRow>) => {
    table.setCreatingRow(null);
    setCreatingRow(false);
    table.setEditingRow(null);
    setEditingRow(null);
    setTemplateName("");
    setTemplateDescription("");
    setTemplateResources([]);
    table.resetExpanded();
  }, []);

  const renderTopToolbarCustomActions = useCallback(
    (tableProps: { table: TableInstance<TableRow> }) => {
      const filterStateWithoutGrantee = (filters: ColumnFiltersState) =>
        (filters ?? []).filter((f) => f.id !== "grantee");
      const filters: JSX.Element | undefined =
        !isSingleGranteeView && !creatingRow && !isManageTemplatesView ? (
          <Autocomplete
            multiple={false}
            options={granteeOptions}
            getOptionLabel={(o) => o?.label ?? ""}
            isOptionEqualToValue={(o, v) => o?.value === v?.value}
            value={(() => {
              const f = (columnFilters ?? []).find((f) => f.id === "grantee");
              return f ? granteeOptions.find((o) => o.value === f.value) ?? null : null;
            })()}
            onChange={(_, newValue) => {
              setColumnFilters((prev) => {
                const without = filterStateWithoutGrantee(prev);

                if (!newValue) {
                  return without;
                }

                return [...without, { id: "grantee", value: newValue.value, type: "equals" as const }];
              });
            }}
            renderInput={(params) => <TextField {...params} label="Filter by Grantee" size="small" />}
            sx={{
              minWidth: 200,
              maxWidth: 340,
              mb: 1,
              mt: 1.5,
            }}
          />
        ) : undefined;
      const additionalActions: JSX.Element = (
        <PermissionsToolbar
          table={tableProps.table}
          search={location.search}
          navigate={navigate}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={!!creatingRow}
          isManageTemplatesView={isManageTemplatesView}
          isFilterPanelOpen={isFilterPanelOpen}
          onToggleFilterPanel={() => setIsFilterPanelOpen((v) => !v)}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          hasParent={hasParent}
          individualByUserId={individualByUserId}
          documentById={documentById}
          proposalById={proposalById}
          workAgreementById={workAgreementById}
          onBackButtonClicked={() => {
            clearToMainPermissionsView();
            resetState(tableProps.table);
          }}
          onClearFiltersClicked={handleClearFiltersClicked}
        />
      );

      return props.renderTopToolbarCustomActions(tableProps, additionalActions, filters);
    },
    [
      isSingleGranteeView,
      creatingRow,
      isManageTemplatesView,
      granteeOptions,
      location.search,
      navigate,
      isFilterPanelOpen,
      activeCategory,
      hasParent,
      individualByUserId,
      documentById,
      proposalById,
      workAgreementById,
      resetState,
      handleClearFiltersClicked,
      clearToMainPermissionsView,
      setActiveCategory,
      props,
      columnFilters,
      setColumnFilters,
    ]
  );

  // Child constructors and child types present
  const childAccessTypes = useMemo(() => childResourceConstructors(), []);

  // Which child resource types exist in the current page of data
  const childTypesInData: ResourceConstructor[] = useMemo<ResourceConstructor[]>((): ResourceConstructor[] => {
    const resourceSet = new Set<ResourceConstructor>();

    for (const row of permissionsResponse?.data ?? []) {
      for (const permission of row.recentPermissionsPreview ?? []) {
        if (!isTopLevelParent(permission)) {
          resourceSet.add(permission.resource.constructor as ResourceConstructor);
        }
      }
    }

    for (const accessType of childAccessTypes) resourceSet.add(accessType);

    return Array.from(resourceSet).sort((a, b) =>
      classNameFromResource(a).localeCompare(classNameFromResource(b), undefined, { sensitivity: "base" })
    );
  }, [permissionsResponse?.data, childAccessTypes]);

  const getCardSubheader = useCallback((resource: Resource): string | undefined => {
    if (resource instanceof DocumentAccess) {
      return getDocumentName(resource, loadingDocumentIds, allDocumentErrors, documents);
    }
    if (resource instanceof ForumAccess) {
      return getForumName(resource, loadingForumIds, allForumErrors, forums);
    }
    if (resource instanceof EntityAgreementAccess) {
      return getEntityAgreementName(resource, loadingEntityAgreementIds, allEntityAgreementErrors, entityAgreements);
    }
    if (resource instanceof ProposalAccess) {
      return getProposalName(
        resource,
        proposalQuery.isLoading,
        [], // TODO: Surface proposal errors from RTK Query
        barebonesProposals
      );
    }
    if (resource instanceof WorkAgreementAccess) {
      return getWorkAgreementName(resource, loadingWorkAgreementIds, allWorkAgreementErrors, barebonesWorkAgreements);
    }
    if (resource instanceof LegalEntityAccess) {
      return session.user!.companyName
    }
    return undefined;
  }, [
    loadingDocumentIds,
    allDocumentErrors,
    documents,
    loadingForumIds,
    allForumErrors,
    forums,
    loadingEntityAgreementIds,
    allEntityAgreementErrors,
    entityAgreements,
    proposalQuery.isLoading,
    barebonesProposals,
    loadingWorkAgreementIds,
    allWorkAgreementErrors,
    barebonesWorkAgreements,
    session.user
  ]);

  const getCandidates = useCallback((row: TableRow, ctor: ResourceConstructor) => {
    if (isPermissionsRow(row)) {
      return row.recentPermissionsPreview.filter(
        (permission) =>
          permission.resource instanceof ctor &&
          (hasParent ? !isTopLevelParent(permission) : isTopLevelParent(permission))
      );
    }

    if (isTemplateRow(row)) {
      let source: Resource[];
      const isCreatingThisRow = creatingRow && row.id?.isEqualTo?.(Guid.EMPTY);
      const isEditingThisRow = Boolean(editingRow?.original) && editingRow!.original.id?.isEqualTo?.(row.id);

      if (isEditingThisRow || isCreatingThisRow) {
        source = templateResources;
      } else {
        source = row.resources;
      }
      return source.filter((res) => res instanceof ctor) ?? [];
    }

    return [];
  }, [hasParent, creatingRow, editingRow, templateResources]);

  const renderSingleTopLevelPermission = useCallback(
    (row: TableRow | null, resource: ResourceConstructor, canEdit: boolean): JSX.Element => {
      const config: UniversalCellConfig = {
      cardType: CardType.Single,
      resource,
      candidates: getCandidates,
      template: (ctor) => topLevelTemplates.byConstructor.get(ctor),
      title: ({ resource, permission }) => {
        if (isSingleGranteeView || creatingRow || isManageTemplatesView) {
          return classNameFromResource(resource);
        }
        return permission.isDisabled ? "Disabled" : "Enabled";
      },
      colSpan: 1,
      rowSpan: 1,
      canEdit,
    };
    return (
      <PermissionCard
        key={resource.name}
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }, [
    getCandidates, 
    isSingleGranteeView, 
    creatingRow, 
    isManageTemplatesView, 
    theme, 
    activeCategory, 
    individuals, 
    loadingIndividualUserIds, 
    getCardSubheader, 
    handleTogglePermission, 
    handleToggleTemplateResource, 
    applyAccessControlChange, 
    handleTemplateAccessControlChange, 
    topLevelTemplates.byConstructor
   ]
  );

  const renderGroupedPermission = useCallback((row: TableRow, config: GroupedCellConfig): JSX.Element => {
    return (
      <PermissionCard
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }, [
    isSingleGranteeView, 
    creatingRow, 
    isManageTemplatesView, 
    theme, 
    activeCategory, 
    individuals, 
    loadingIndividualUserIds, 
    getCardSubheader, 
    handleTogglePermission, 
    handleToggleTemplateResource, 
    applyAccessControlChange, 
    handleTemplateAccessControlChange
  ]);

  const renderSinglePermission = useCallback(
    (row: TableRow | null, resource: ResourceConstructor, canEdit: boolean): JSX.Element | null => {
      if (!row) return null;
    const config: UniversalCellConfig = {
      cardType: CardType.Single,
      resource,
      candidates: getCandidates,
      title: ({ resource, permission }) => {
        if (isSingleGranteeView || creatingRow || isManageTemplatesView) {
          return classNameFromResource(resource);
        }
        return permission.isDisabled ? "Disabled" : "Enabled";
      },
      colSpan: 1,
      rowSpan: 1,
      canEdit,
    };
    return (
      <PermissionCard
        row={row}
        config={config}
        isSingleGranteeView={isSingleGranteeView}
        isCreateTemplateView={!!creatingRow}
        isManageTemplatesView={isManageTemplatesView}
        theme={theme}
        activeCategory={activeCategory}
        individuals={individuals}
        loadingIndividualUserIds={loadingIndividualUserIds}
        getCardSubheader={getCardSubheader}
        onTogglePermission={handleTogglePermission}
        onToggleTemplate={handleToggleTemplateResource}
        onApplyPermissionMeta={applyAccessControlChange}
        onApplyTemplateMeta={handleTemplateAccessControlChange}
      />
    );
  }, [
    getCandidates, 
    isSingleGranteeView, 
    creatingRow, 
    isManageTemplatesView, 
    theme, 
    activeCategory, 
    individuals, 
    loadingIndividualUserIds, 
    getCardSubheader, 
    handleTogglePermission, 
    handleToggleTemplateResource, 
    applyAccessControlChange, 
    handleTemplateAccessControlChange
  ]);

  const applyTemplateToGrantees = useCallback(async (template: PermissionTemplate, userIds: Guid[]) => {
    // TODO: Allow editing template before applying
    // TODO: Show diff of permission changes for each user

    const response = await confirm({
      title: "Overwrite Existing Permissions?",
      message:
        "Applying a permissions template will overwrite all existing permissions for the selected user(s). Continue?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const entityId = session.user!.legalEntityId;
      const currentUserId = session.user?.id;
      const resources = template.resources;

      const newPermissions = [];
      for (const userId of userIds) {
        for (const resource of resources) {
          newPermissions.push(
            new Permission({
              resource,
              entityId: entityId,
              granteeUserId: userId,
              grantorUserId: currentUserId ?? Guid.EMPTY
            })
          );
        }
      }
      dispatch(addPermissions({ session, permissions: newPermissions }));
      showToast("Permissions updated from template", "success");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        showToast("Unable to save Permission: " + error.response.data, "warning");
      } else {
        console.error(error);
        showToast("Unable to save Permission", "error");
      }
    }
  }, [confirm, dispatch, session]);

  const renderGranteeColumn = useCallback(
    (granteeUserId: Guid | undefined, tableInstance: TableInstance<TableRow>) => {
      if (!granteeUserId || granteeUserId.isEqualTo?.(Guid.EMPTY)) {
        return <GranteeNameCell userId={Guid.EMPTY} name="(Unknown)" isLoading={false} error={null} />;
      }
      const key = granteeUserId.value;
      const granteeName = individualByUserId.get(key)?.fullName;
      const isLoading = loadingUserIdsSet.has(key);
      const error = individualErrorByUserId.get(key) ?? null;

      if (isSingleGranteeView) {
        return (
          <Box>
            <Typography variant="h6" sx={{ pb: 1.5 }}>
              {isLoading ? "Loading..." : granteeName ?? "(Unknown)"}
            </Typography>
            <ApplyTemplateMenuButton
              key={key}
              disabled={session.user ? granteeUserId.isEqualTo(session.user.id) : false}
              onApply={(template) => {
                tableInstance.resetRowSelection();
                applyTemplateToGrantees(template, [granteeUserId]);
              }}
            />
          </Box>
        );
      }

      return <GranteeNameCell userId={granteeUserId} name={granteeName} isLoading={isLoading} error={error} />;
    },
    [individualByUserId, loadingUserIdsSet, individualErrorByUserId, isSingleGranteeView, session.user, applyTemplateToGrantees]
  );

  const NameCellRenderer = useCallback(
    ({ cell, row }: { cell: Cell<TableRow>; row: Row<TableRow> }) => {
      const isCreatingThisRow = creatingRow && row.original.id.isEqualTo(Guid.EMPTY);
      const isEditingThisRow = isCreatingThisRow || editingRow?.id === row.id;

      return (
        <NameCell
          key={`${row.id}-${isEditingThisRow ? "edit" : "view"}`}
          ref={isEditingThisRow ? nameCellRef : null}
          label={String(cell.column.columnDef.header ?? "Name")}
          name={(row.original as PermissionTemplate).name}
          isReadOnly={!isEditingThisRow && !row.original.id?.isEqualTo(Guid.EMPTY)}
          onValidityChange={isEditingThisRow ? setNameIsValid : undefined}
        />
      );
    },
    [creatingRow, editingRow?.id]
  );

  const DescriptionCellRenderer = useCallback(
    ({ cell, row }: { cell: Cell<TableRow>; row: Row<TableRow> }) => {
      const isCreatingThisRow = creatingRow && row.original.id.isEqualTo(Guid.EMPTY);
      const isEditingThisRow = isCreatingThisRow || editingRow?.id === row.id;
      const description = (row.original as PermissionTemplate).description;
      if (!isEditingThisRow && !description) {
        return (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontStyle: "italic",
            }}
          >
            No description
          </Typography>
        );
      }
      return (
        <DescriptionCell
          key={`${row.id}-${isEditingThisRow ? "edit" : "view"}`}
          ref={isEditingThisRow ? descriptionCellRef : null}
          label={String(cell.column.columnDef.header ?? "Description")}
          description={description}
          isReadOnly={!isEditingThisRow && !row.original.id.isEqualTo(Guid.EMPTY)}
        />
      );
    },
    [creatingRow, editingRow?.id, theme.palette.text.secondary]
  );

  const CreatedDateCellRenderer = useCallback(({ row }: { row: Row<TableRow> }) => {
    const createdDate = (row.original as PermissionTemplate).createdDate;
    return (
      <Typography variant="body1" sx={{ alignContent: "center", textAlign: "left" }}>
        {createdDate?.format("MM/DD/YYYY") ?? <Icon>remove</Icon>}
      </Typography>
    );
  }, []);

  const PermissionsCellRenderer = useCallback(
    ({ row }: { row: Row<TableRow> }) => {
      if (creatingRow && !row.original.id.isEqualTo(Guid.EMPTY)) return;
      return (
        <SingleGranteeResourceCell
          row={row.original}
          topLevelResources={topLevelResources}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={row?.id === editingRow?.id || (creatingRow && row.original.id.isEqualTo(Guid.EMPTY))}
          renderLegalEntityCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <LegalEntityGroupCell
              key="legal-entity-group-cell"
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              canEdit={canEdit}
              creatingRow={creatingRow}
              editingRow={editingRow}
              resources={templateResources}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderDocumentSubjectMatterCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <DocumentSubjectMatterGroupCell
              key="document-subject-matter-group-cell"
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              canEdit={canEdit}
              creatingRow={creatingRow}
              editingRow={editingRow}
              resources={templateResources}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderSingleTopLevelPermission={renderSingleTopLevelPermission}
        />
      );
    },
    [
      creatingRow, 
      topLevelResources, 
      topLevelTemplates, 
      isSingleGranteeView, 
      isManageTemplatesView, 
      editingRow, 
      renderSingleTopLevelPermission, 
      templateResources, 
      renderGroupedPermission
    ]
  );

  const columns = useMemo(() => {
    const columnsToShow = [];

    if (!creatingRow && !isManageTemplatesView) {
      const granteeCell = {
        id: "grantee",
        header: "Grantee",
        muiTableHeadCellProps: { align: "left" },
        muiTableBodyCellProps: {
          sx: {
            alignItems: "start",
            borderBottom: isSingleGranteeView ? 0 : undefined,
            borderTop: isSingleGranteeView ? 0 : undefined,
            justifyContent: "center",
            minWidth: 'fit-content !important'
          },
        },
        enableColumnFilter: false,
        grow: 0,
        Cell: ({ row, table }) =>
          isPermissionsRow(row.original) ? renderGranteeColumn(row.original.granteeUserId, table) : null,
      } satisfies ColumnDef<TableRow, string>;
      columnsToShow.push(granteeCell);
    }

    if (isManageTemplatesView) {
      const manageTemplateColumns = [
        columnHelper.display({
          id: "name",
          header: "Name",
          grow: 0,
          muiTableBodyCellProps: {
            sx: { textAlign: "left" },
            tabIndex: creatingRow || editingRow ? -1 : undefined,
          },
          Cell: NameCellRenderer,
        }),
        columnHelper.display({
          id: "description",
          header: "Description",
          grow: 0,
          muiTableBodyCellProps: {
            sx: { textAlign: "left" },
            tabIndex: creatingRow || editingRow ? -1 : undefined,
          },
          Cell: DescriptionCellRenderer,
        }),
      ];
      if (!creatingRow) {
        columnsToShow.push(
          columnHelper.display({
            id: "createdDate",
            header: "Created Date",
            grow: 0,
            enableEditing: false,
            Cell: CreatedDateCellRenderer,
          })
        );
      }
      columnsToShow.push(...manageTemplateColumns);
    }

    if (isSingleGranteeView || creatingRow) {
      columnsToShow.push(
        columnHelper.display({
          id: "permissions",
          header: "Permissions",
          visibleInShowHideMenu: false,
          enableColumnActions: false,
          grow: 1,
          muiTableBodyCellProps: {
            sx: {
              borderBottom: isSingleGranteeView ? 0 : undefined,
              whiteSpace: "nowrap",
              width: "max-content",
              minWidth: 0,
            },
          },
          Cell: PermissionsCellRenderer,
        })
      );
      return columnsToShow;
    }

    if (isManageTemplatesView) return columnsToShow;

    // Build column definitions
    function makeColumn(id: string, header: string, Cell: (row: Row<TableRow> | null) => JSX.Element | null) {
      return columnHelper.display({
        id,
        header,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ row }) => Cell(row),
      });
    }

    const buildGenericResourceColumn = (resource: ResourceConstructor) =>
      makeColumn(`rt:${resource.name}`, classNameFromResource(resource), (row) =>
        renderSinglePermission(row?.original ?? null, resource, row?.id === editingRow?.id)
      );

    const buildDocumentSubjectMatterColumn = () =>
      makeColumn("group:DocumentContents", "Document Contents", (row) => (
        <DocumentSubjectMatterGroupCell
          row={row?.original ?? null}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={!!creatingRow}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={row?.id === editingRow?.id}
          resources={templateResources}
          creatingRow={creatingRow}
          editingRow={editingRow}
          renderGroupedPermission={renderGroupedPermission}
        />
      ));

    const buildLegalEntitiesColumn = () =>
      makeColumn("group:LegalEntities", "Legal Entities", (row) => (
        <LegalEntityGroupCell
          row={row?.original ?? null}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={!!creatingRow}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={row?.id === editingRow?.id}
          resources={templateResources}
          creatingRow={creatingRow}
          editingRow={editingRow}
          renderGroupedPermission={renderGroupedPermission}
        />
      ));

    const buildProposalColumn = () =>
      makeColumn("group:Proposal", "Proposal", (row) =>
        row?.original ? (
          <ProposalGroupCell
            row={row.original as PermissionsByGrantee}
            loadingProposalIds={proposalQuery.isLoading}
            allProposalErrors={[]}  // TODO: Surface proposal errors from RTK Query
            barebonesProposals={barebonesProposals}
            renderGroupedPermission={renderGroupedPermission}
          />
        ) : null
      );

    const buildWorkAgreementColumn = () =>
      makeColumn("group:WorkAgreement", "Work Agreement", (row) => (
        <WorkAgreementGroupCell
          row={row?.original as PermissionsByGrantee | null}
          loadingWorkAgreementIds={loadingWorkAgreementIds}
          allWorkAgreementErrors={allWorkAgreementErrors}
          barebonesWorkAgreements={barebonesWorkAgreements}
          renderGroupedPermission={renderGroupedPermission}
        />
      ));

    const topLevelColumnDefs: SortedColumnDef[] = (() => {
      if (hasParent || hasAdditionalFilters) {
        // Hide top-level permissions
        return [];
      }

      return topLevelResources.map<SortedColumnDef>((resource: ResourceConstructor) => {
        if (resource === LegalEntityAccess) {
          return {
            sortKey: "Legal Entities",
            columnDefinition: buildLegalEntitiesColumn(),
          };
        }
        if (resource === DocumentContentsAccess) {
          return {
            sortKey: "Document Contents",
            columnDefinition: buildDocumentSubjectMatterColumn(),
          };
        }
        return {
          sortKey: classNameFromResource(resource),
          columnDefinition: buildGenericResourceColumn(resource),
        };
      });
    })();

    const childColumnDefs: SortedColumnDef[] = (() => {
      // Allow child columns when filtered -OR- when viewing children (hasParent)
      if (!hasParent && !hasAdditionalFilters) return [];

      return childTypesInData.map((Ctor): SortedColumnDef => {
        if (Ctor === ProposalAccess) {
          return {
            sortKey: "Proposal",
            columnDefinition: buildProposalColumn(),
          };
        }
        if (Ctor === WorkAgreementAccess) {
          return {
            sortKey: "Work Agreement",
            columnDefinition: buildWorkAgreementColumn(),
          };
        }
        return {
          sortKey: classNameFromResource(Ctor),
          columnDefinition: buildGenericResourceColumn(Ctor),
        };
      });
    })();

    const merged = [...topLevelColumnDefs, ...childColumnDefs].sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey, undefined, { sensitivity: "base" })
    );

    for (const { columnDefinition } of merged) {
      columnsToShow.push(columnDefinition);
    }

    return columnsToShow;
  }, [
    creatingRow, 
    isManageTemplatesView, 
    isSingleGranteeView, 
    renderGranteeColumn,  
    editingRow, 
    NameCellRenderer, 
    DescriptionCellRenderer, 
    CreatedDateCellRenderer, 
    PermissionsCellRenderer, 
    renderSinglePermission, 
    topLevelTemplates, 
    templateResources, 
    renderGroupedPermission, 
    proposalQuery.isLoading, 
    barebonesProposals, 
    loadingWorkAgreementIds, 
    allWorkAgreementErrors, 
    barebonesWorkAgreements, 
    hasParent, 
    hasAdditionalFilters, 
    topLevelResources, 
    childTypesInData
  ]);

  const headersById = useMemo(() => {
    const m = new Map<string, string>();
    for (const col of columns) {
      const id = (col.id ?? String(col.accessorKey ?? "")).trim();
      const headerText = col.header ?? id ?? "";
      if (id) m.set(id, headerText);
    }
    return m;
  }, [columns]);

  const derivedColumnVisibility = useMemo(() => {
    const totals = sumPermissionCounts(permissionsResponse);
    let next: Record<string, boolean> = { grantee: true };
    next = {
      ...next,
      ...computeTopLevelVisibility(topLevelResources, totals, { hasParent, hasAdditionalFilters }),
    };
    next = {
      ...next,
      ...computeChildVisibility(childTypesInData, hasAdditionalFilters || hasParent),
    };

    return clampByCategory(next, headersById, activeCategory);
  }, [
    topLevelResources, 
    childTypesInData, 
    hasParent, 
    hasAdditionalFilters, 
    activeCategory, 
    headersById, 
    permissionsResponse
  ]);

  const tableData = useMemo(() => {
    if (isManageTemplatesView) {
      const rows = [...(permissionTemplatesResponse?.data ?? [])];

      if (creatingRow) {
        rows.unshift(
          new PermissionTemplate({
            id: Guid.EMPTY,
            userId: Guid.generate(),
            resources: Array.from(topLevelTemplates.byConstructor.values()),
            name: "",
            description: "",
            createdDate: new AHDate(),
          })
        );
      }

      return rows;
    }

    return [...(permissionsResponse?.data ?? [])];
  }, [
    isManageTemplatesView, 
    permissionsResponse?.data, 
    permissionTemplatesResponse?.data, 
    creatingRow, 
    topLevelTemplates.byConstructor
  ]);

  function handleTemplateEdit(
    table: ReturnType<typeof useMaterialReactTable<TableRow>>, 
    row: Row<TableRow>, 
    template: PermissionTemplate
  ) {
    table.setEditingRow(row);
    setEditingRow(row);

    const clonedResources = template.resources.map((r) => resourceFromJSON(r.toJSON() as AnyResourceJSON));

    setTemplateName(template.name);
    setTemplateDescription(template.description ?? "");
    setTemplateResources(clonedResources.filter((r): r is Resource => r !== null));
    setNameIsValid((template.name ?? "").trim().length > 0);
    table.setExpanded({ [row.id]: true });
  }

  function handleTemplateSave(
    table: ReturnType<typeof useMaterialReactTable<TableRow>>,
    resetState: (table: ReturnType<typeof useMaterialReactTable<TableRow>>) => void
  ) {
    if (!creatingRow && !editingRow) return;

    const draftedName = nameCellRef.current?.getValue()?.trim();
    const draftedDescription = descriptionCellRef.current?.getValue()?.trim();

    const finalName = draftedName && draftedName.length > 0 ? draftedName : templateName?.trim();
    const finalDescription =
      draftedDescription && draftedDescription.length > 0 ? draftedDescription : templateDescription?.trim();

    if (creatingRow && (!finalName || finalName.length === 0)) {
      showToast("Template name is required.", "error");
      return;
    }
    if (!session.user?.id) {
      showToast("Unable to determine current user.", "error");
      return;
    }

    if (finalName && finalName !== templateName) {
      setTemplateName(finalName);
    }
    if (finalDescription && finalDescription !== templateDescription) {
      setTemplateDescription(finalDescription);
    }

    const newTemplate = new PermissionTemplate({
      id: editingRow ? editingRow.original.id : Guid.generate(),
      userId: session.user.id,
      resources: templateResources,
      name: finalName ?? "",
      description: finalDescription ?? "",
    });

    const action = creatingRow
      ? addPermissionTemplate({ session, template: newTemplate })
      : updatePermissionTemplate({ session, template: newTemplate });

    dispatch(action)
      .unwrap()
      .then(() => {
        dispatch(populatePermissionTemplatesByQuery({ session, query: permissionTemplatesQuery }));
        showToast(creatingRow ? "Template created" : "Template updated", "success");
      })
      .catch((error) => showToast(error, "error"))
      .finally(() => resetState(table));
  }

  function handleTemplateDelete(
    table: ReturnType<typeof useMaterialReactTable<TableRow>>, 
    templateId: Guid, 
    resetState: (table: ReturnType<typeof useMaterialReactTable<TableRow>>) => void
  ) {
    dispatch(removePermissionTemplate({ session, templateId }))
      .unwrap()
      .then(() => {
        dispatch(populatePermissionTemplatesByQuery({ session, query: permissionTemplatesQuery }));
        showToast("Template deleted", "info");
      })
      .catch((error) => showToast(error, "error"))
      .finally(() => resetState(table));
  }

  const tableOptions: MRT_TableOptions<TableRow> = {
    columns: columns,
    data: tableData,
    enableRowSelection:
      !isManageTemplatesView && !creatingRow && !isSingleGranteeView
        ? (row) => isPermissionsRow(row.original) && !row.original.granteeUserId.isEqualTo(session.user?.id)
        : undefined,
    enableMultiRowSelection:
      !isManageTemplatesView && !creatingRow && !isSingleGranteeView
        ? (row) => isPermissionsRow(row.original) && !row.original.granteeUserId.isEqualTo(session.user?.id)
        : undefined,
    enableTableHead: !isSingleGranteeView,
    enableRowActions: isManageTemplatesView,
    enableColumnPinning: !isSingleGranteeView,
    enableEditing: true,
    displayColumnDefOptions: {
      "mrt-row-select": {
        grow: 0,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      "mrt-row-expand": {
        grow: 0,
        muiTableHeadCellProps: {
          sx: {
            align: "left",
            display: creatingRow || !isManageTemplatesView ? "none" : "table-cell",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            align: "left",
            display: creatingRow || !isManageTemplatesView ? "none" : "table-cell",
          },
        },
      },
      "mrt-row-actions": {
        grow: 0,
        muiTableHeadCellProps: {
          sx: {
            "& .Mui-TableHeadCell-Content": {
              justifyContent: "flex-end",
            },
            minWidth: 110,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            gap: 0,
            "& > *": {
              gap: 0,
              height: "auto",
              width: "auto",
            },
            minWidth: 110,
            marginRight: 1,
          },
        },
      },
    },
    createDisplayMode: "custom",
    editDisplayMode: "custom",
    initialState: {
      columnFilters,
      columnPinning: {
        left: isSingleGranteeView ? ["mrt-row-select"] : ["mrt-row-select", "grantee"],
      },
      columnOrder: [
        "mrt-row-select",
        "grantee",
        "name",
        "description",
        "createdDate",
        "permissions",
        ...columns
          .filter(
            (c) =>
              c.id !== "grantee" &&
              c.id !== "name" &&
              c.id !== "description" &&
              c.id !== "createdDate" &&
              c.id !== "permissions"
          )
          .map((c) => c.id ?? String(c.accessorKey ?? "")),
      ],
      columnVisibility: derivedColumnVisibility,
      density: "compact",
    },
    positionActionsColumn: "last",
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    enableExpandAll: false,
    manualPagination: true,
    manualSorting: true,
    layoutMode: isSingleGranteeView || creatingRow ? "grid" : "semantic",
    columnFilterDisplayMode: "subheader",
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiSelectCheckboxProps: { disableRipple: true },
    muiSelectAllCheckboxProps: { disableRipple: true },
    muiTableContainerProps: ({ table }) => {
      const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
      const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;
      return {
        sx: {
          height: `calc(100% - ${top}px - ${bottom}px)`,
        },
      };
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: () => {
      if (
        permissionsLoadErrors ||
        permissionTemplatesLoadErrors ||
        areErrorsLoadingDocuments ||
        areErrorsLoadingForums ||
        proposalQuery.isError ||
        areErrorsLoadingWorkAgreements ||
        areErrorsLoadingIndividuals ||
        (sessionPermissions?.includesAccessToAdministration && areErrorsLoadingEntityAgreements)
      ) {
        return {
          color: "error",
          children: "Error loading data",
        } satisfies AlertProps;
      }
      return {} satisfies AlertProps;
    },
    muiTableBodyRowProps: ({ row, isDetailPanel }) => ({
      hover: false,
      sx: {
        height: (() => {
          if (row.original.id?.isEqualTo(Guid.EMPTY) || isDetailPanel) {
            return "auto";
          }
          if (isManageTemplatesView) return "66px";
          if (isSingleGranteeView) return "auto";
          return "135px";
        })(),
        "&:hover": { backgroundColor: "unset" },
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        height: "inherit",
        textAlign: "center",
        padding: 0.4,
        "& > *": {
          height: "100%",
        },
      },
    },
    muiDetailPanelProps: {
      sx: {
        whiteSpace: "nowrap",
        width: "max-content",
        minWidth: 0,
      },
    },
    muiExpandButtonProps: ({ row, table }) => ({
      disabled: !!editingRow || row.original.id?.isEqualTo(Guid.EMPTY),
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }), // Only 1 detail panel open at a time
      sx: {
        transform: row.getIsExpanded() ? "rotate(180deg)" : "rotate(-90deg)",
        transition: "transform 0.2s",
      },
    }),
    renderDetailPanel: ({ row }) => {
      if (!isManageTemplatesView) return;
      return (
        <SingleGranteeResourceCell
          row={row.original}
          topLevelResources={topLevelResources}
          topLevelTemplates={topLevelTemplates}
          isSingleGranteeView={isSingleGranteeView}
          isManageTemplatesView={isManageTemplatesView}
          canEdit={editingRow?.id === row.id}
          renderLegalEntityCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <LegalEntityGroupCell
              key="legal-entity-group-cell"
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              canEdit={!!canEdit}
              resources={templateResources}
              creatingRow={creatingRow}
              editingRow={editingRow}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderDocumentSubjectMatterCell={(row, topLevelTemplates, isSingleGranteeView, canEdit) => (
            <DocumentSubjectMatterGroupCell
              key="document-subject-matter-group-cell"
              row={row}
              topLevelTemplates={topLevelTemplates}
              isSingleGranteeView={isSingleGranteeView}
              isCreateTemplateView={!!creatingRow}
              isManageTemplatesView={isManageTemplatesView}
              canEdit={!!canEdit}
              resources={templateResources}
              creatingRow={creatingRow}
              editingRow={editingRow}
              renderGroupedPermission={renderGroupedPermission}
            />
          )}
          renderSingleTopLevelPermission={renderSingleTopLevelPermission}
        />
      );
    },
    renderRowActions: ({ table, row }) => {
      const originalRow = row.original as PermissionTemplate;

      return (
        <ManageTemplateRowActions
          templateId={originalRow.id}
          isCreating={creatingRow}
          editingRow={editingRow}
          isLoading={isLoadingPermissionTemplates}
          canSave={nameIsValid}
          onEdit={() => handleTemplateEdit(table, row, originalRow)}
          onSave={() => handleTemplateSave(table, resetState)}
          onCancel={() => {
            setNameIsValid(false);
            resetState(table);
          }}
          onDelete={(templateId) => handleTemplateDelete(table, templateId, resetState)}
        />
      );
    },
    renderToolbarAlertBannerContent: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().flatRows.map((r) => r.original);
      const count = selectedRows.length;
      if (count === 0) return null;
      return (
        <AlertBarContainer>
          <AlertBarSelectionContainer>
            <Typography>{count} user(s) selected</Typography>
            <Button sx={{ ml: 2 }} size="small" color="primary" onClick={() => table.resetRowSelection()}>
              Clear Selection
            </Button>
          </AlertBarSelectionContainer>
          <ApplyTemplateMenuButton
            key="toolbar"
            onApply={(template) => {
              table.resetRowSelection();
              applyTemplateToGrantees(
                template,
                Array.from(new Set((selectedRows as PermissionsByGrantee[]).map((r) => r.granteeUserId.value))).map(
                  (id) => new Guid(id)
                )
              );
            }}
          />
        </AlertBarContainer>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            {!isManageTemplatesView && !creatingRow && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  navigate("/administration/permissions?action=manage-templates");
                }}
              >
                Manage Templates
              </Button>
            )}
            {!creatingRow && !isManageTemplatesView && (
              <Button
                color="primary"
                startIcon={<AddModeratorIcon />}
                variant="outlined"
                onClick={openAddPermissionDialog}
              >
                Add Permission
              </Button>
            )}
            {!creatingRow && isManageTemplatesView && (
              <Button
                color="primary"
                startIcon={<PostAddIcon />}
                variant="outlined"
                onClick={() => {
                  const initialResources = [
                    ...Array.from(topLevelTemplates.byConstructor.values().map((r) => resourceFromJSON(r.toJSON() as AnyResourceJSON))),
                    ...Array.from(topLevelTemplates.docContentsBySubject.values().map((r) => resourceFromJSON(r.toJSON() as AnyResourceJSON))),
                    ...Array.from(topLevelTemplates.legalByAccessType.values().map((r) => resourceFromJSON(r.toJSON() as AnyResourceJSON))),
                  ];
                  table.toggleAllRowsExpanded(false);
                  table.setCreatingRow(true);
                  setCreatingRow(true);
                  setTemplateName("");
                  setTemplateDescription("");
                  setTemplateResources(initialResources.filter((r): r is Resource => r !== undefined));
                  setNameIsValid(false);
                }}
              >
                Create Template
              </Button>
            )}
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <>
            <MobileButton color="primary">
              <ContentCopyIcon fontSize="large" />
            </MobileButton>
            <MobileButton color="primary" onClick={openAddPermissionDialog}>
              <AddBoxIcon fontSize="large" />
            </MobileButton>
          </>
        )}
      </>
    ),
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderEmptyRowsFallback: isManageTemplatesView
      ? () => <NoRowsText>No Permission Templates Found</NoRowsText>
      : undefined,
    rowCount: isManageTemplatesView
      ? permissionTemplatesResponse?.totalElements ?? 0
      : permissionsResponse?.totalElements ?? 0,
    state: {
      editingRow,
      columnFilters,
      showColumnFilters: true,
      columnVisibility: derivedColumnVisibility,
      globalFilter,
      isLoading:
        (!permissionsResponse?.data.length && isLoadingPermissions) ||
        (!permissionTemplatesResponse?.data.length && isLoadingPermissionTemplates),
      pagination,
      showAlertBanner: Boolean(
        permissionsLoadErrors ||
        permissionTemplatesLoadErrors ||
        areErrorsLoadingIndividuals ||
        areErrorsLoadingDocuments ||
        proposalQuery.isError ||
        areErrorsLoadingWorkAgreements ||
        areErrorsLoadingForums ||
        (sessionPermissions?.includesSigningAuthority && areErrorsLoadingEntityAgreements)
      ),
      showProgressBars:
        isLoadingPermissions ||
        isLoadingPermissionTemplates ||
        isRefetching ||
        loadingIndividualUserIds.length > 0 ||
        loadingDocumentIds.length > 0 ||
        proposalQuery.isFetching ||
        loadingWorkAgreementIds.length > 0 ||
        loadingForumIds.length > 0 ||
        loadingEntityAgreementIds.length > 0,
      sorting,
      showSkeletons: !permissionsResponse,
    },
  }

  return (
    <>
      <MRTNoMemo<TableRow> options={tableOptions} />
      <Portal>
        <PermissionsFilterDrawer
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          onFilter={() => { }}
        />
      </Portal>
      <Portal>
        <Snackbar open={toastOpen} autoHideDuration={5000} onClose={() => setToastOpen(false)}>
          <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} variant="filled" sx={{ width: "100%" }}>
            {toastMsg}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
}
